<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import moment from "moment";

import TimelineItemCollection, { ClickChartDataProvider } from "@Modules/analytics/DTOs/TimelineItemCollection";
import useTranslation from "@Composables/useTranslation";
import { useHandleResize, ResizableChart } from "@Composables/useHandleResize";
import { VueECharts as VChart } from "@Helpers/echarts";
import ClickStat from "@Modules/analytics/DTOs/ClickStat";
import ChartLayout from "@Modules/analytics/Components/ChartLayout.vue";

//
// Props
interface Props {
  dataset?: ClickChartDataProvider;
  loading?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  dataset: () => new TimelineItemCollection([]),
  loading: false,
});

//
// Setup
const { __ } = useTranslation();

//
// Data
const data = reactive<{
    interval: string
}>({
    interval: "hour",
});

const chart = ref<ResizableChart | null>(null);

useHandleResize(chart);

/**
 * Converts the analytics data into the format expected by the chart
*/
const clicks = computed(() => props.dataset?.getChartData());

const datetimeFormat = computed(() => {
    if (data.interval === "minute") {
        return {
            short: "h:mm a",
            long: "MMM Do h:mm a",
        };
    } else if (data.interval === "hour") {
        return {
            short: "h a",
            long: "MMM Do h a",
        };
    } else if (data.interval === "day") {
        return {
            short: "MMM Do",
            long: "MMM Do YYYY",
        };
    } else if (data.interval === "week") {
        return {
            short: "YYY Wo",
            long: "MMM YYYY, Wo",
        };
    } else if (data.interval === "month") {
        return {
            short: "YYY Mo",
            long: "MMM YYYY",
        };
    } else {
        return {
            short: "Y-MM-DD HH:mm",
            long: "Y-MM-DD HH:mm",
        };
    }
});

const dateTimeFormatAxis = computed(() => {
    const formatString = datetimeFormat.value

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- echarts v4 formatter callback params are untyped
    return (text: string, index: any): string => {
        let formated = moment(text).format(formatString.short);

        if (index === undefined || index === 0) {
            formated = moment(text).format(formatString.long);
        }

        return formated;
    };
});

const chartOptions = () => {
    return {
        legend: {
            top: "0",
        },
        dataZoom: [
            {
                type: "slider",
                minSpan: 20,
            },
        ],
        grid: {
            left: "6%",
            top: "8%",
            right: "2%",
            bottom: "0",
            containLabel: true,
        },
        tooltip: {
            trigger: "axis",
            axisPointer: {
                type: "shadow",
            },
            backgroundColor: "rgba(240,240,240,0.8)",
            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- echarts v4 formatter callback params are untyped
            formatter: (params: any) => {
                let content = "";
                // @ts-ignore lodash _.first() return type is not narrowed for the echarts params shape
                const dateText = moment(_.first(params).value[0]).format(
                    datetimeFormat.value.long,
                );

                content += `<div class='p-2 text-dark'>`;
                content += `  <h4 class='mb-1'>${dateText}</h4>`;
                content += `  <p class='mb-1 font-weight-bold'>`;
                content += `    ${__("chart.total_clicks")}: `;
                content += `    ${params[0].data[1]}`;
                content += `  </p>`;

                // bots
                content += `  <p class='mb-1'>`;
                content += `    ${params[2].marker} ${params[2].seriesName}: ${params[0].value[2]}`;
                content += `  </p>`;

                // human
                content += `  <p class='mb-1'>`;
                // content += `    <span style="display: inline-block; margin-right: 5px; border-radius: 10px; width: 10px; height: 10px; background-color: #ff2000;"></span>`;
                content += `    ${__("chart.human_clicks")}: `;
                content += `    ${params[0].data[3]}`;
                content += `  </p>`;

                // non-unique
                content += `  <p class='mb-1'>`;
                content += `    ${params[1].marker} ${params[1].seriesName}: ${params[0].value[5]}`;
                content += `  </p>`;

                // unique
                content += `  <p class='mb-1'>`;
                content += `    ${params[0].marker} ${params[0].seriesName}: ${params[0].value[4]}`;
                content += `  </p>`;
                content += `</div>`;

                return content;
            },
        },
        toolbox: {
            show: true,
            feature: {
                restore: {
                    show: true,
                    title: __("chart.restore"),
                },
                saveAsImage: {
                    show: true,
                    title: __("chart.save_as_image"),
                },
            },
        },
        dataset: {
            source: Object.values(clicks.value).map((item: ClickStat) => {
                // This array format is how the orignal chart handled things. it's best not to touch it
                return [
                    item.name,
                    item.total, //total
                    item.bots, //bots
                    item.human, //human
                    item.unique, //unique
                    item.human - item.unique, //nonUnique (human clicks that aren't unique)
                ];
            }),
            dimensions: [
                "date",
                "total",
                "bots",
                "humans",
                "unique",
                "nonUnique",
            ],
        },
        xAxis: [
            {
                type: "time",
                axisLabel: {
                    formatter: dateTimeFormatAxis.value,
                    rotate: 45,
                },
                splitLine: {
                    show: false,
                },
                axisLine: {
                    show: false,
                },
                axisTick: {
                    length: 10,
                },
                minorTick: {
                    show: true,
                    length: 4,
                },
            },
        ],
        yAxis: [
            {
                type: "value",
                axisLine: {
                    show: false,
                },
                axisTick: {
                    show: false,
                },
            },
        ],
        series: [
            {
                id: "unique_clicks",
                type: "bar",
                stack: "one",
                areaStyle: {
                    opacity: 0.3,
                },
                itemStyle: {
                    color: "#002a61",
                },
                name: __("chart.unique_clicks"),
                encode: { x: "date", y: "unique" },
            },
            {
                id: "nonunique_clicks",
                type: "bar",
                stack: "one",
                areaStyle: {
                    opacity: 0.3,
                },
                name: __("chart.nonunique_clicks"),
                itemStyle: {
                    color: "#9bc225",
                },
                encode: { x: "date", y: "nonUnique" },
            },
            {
                id: "bot_clicks",
                type: "bar",
                stack: "one",
                areaStyle: {
                    opacity: 0.3,
                },
                name: __("chart.bot_clicks"),
                itemStyle: {
                    color: "#def1d5",
                },
                encode: {
                    x: "date",
                    y: "bots",
                },
            },
        ],
    };
};

</script>

<template>
  <ChartLayout :is-loading="loading">
    <template #header>
      Traffic Over Time
    </template>
    <v-chart
      ref="chart"
      class="stats-timeline-chart"
      :options="chartOptions()"
    />
  </ChartLayout>
</template>

<style lang="scss" scoped>
.stats-timeline-chart {
  width: 100%;
  min-height: 150px;
  max-height: 280px;
}
</style>
